import { useState, useEffect } from 'react';
import './App.css';
import './ophilia-theme.css';
import './mobile-optimizations.css';
import { BrowserRouter } from 'react-router-dom';
import axios from 'axios';
import { setupAxiosInterceptors, serverLogout } from './utils/axiosConfig';
import ErrorBoundary from './components/ErrorBoundary';
import AppRoutes from './components/AppRoutes';
import InstallPrompt from './components/InstallPrompt';
import InstallPWA from './components/InstallPWA';
import VersionChecker from './components/VersionChecker';
import InstagramBrowserDetector from './components/InstagramBrowserDetector';
import BioPromptModal from './components/BioPromptModal';
import { Toaster } from './components/ui/sonner';

import { BACKEND_URL } from './config';
const API = `${BACKEND_URL}/api`;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Add loading state

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    // Revoke refresh token server-side (clears HTTP-only cookies)
    await serverLogout();
    // Clear client-side non-sensitive data
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    setIsAuthenticated(false);
  };

  // Set up axios interceptors once when app loads
  useEffect(() => {
    setupAxiosInterceptors(handleLogout);
  }, []);

  // Clean up oversized localStorage data on app load
  useEffect(() => {
    try {
      const user = localStorage.getItem('user');
      if (user) {
        const userData = JSON.parse(user);
        // Check if profilePhoto is a large base64 string
        if (userData.profilePhoto && userData.profilePhoto.startsWith('data:') && userData.profilePhoto.length > 10000) {
          console.log('[App] Cleaning up oversized localStorage data');
          // Remove base64 photo to free up space
          userData.profilePhoto = null;
          localStorage.setItem('user', JSON.stringify(userData));
        }
      }
    } catch (e) {
      console.error('[App] Error cleaning localStorage:', e);
      // If localStorage is corrupted, clear user data
      if (e.name === 'QuotaExceededError' || e.message?.includes('quota')) {
        console.log('[App] Quota exceeded, clearing user data');
        localStorage.removeItem('user');
      }
    }
  }, []);

  // Auth initialization phase - cookie-based refresh is the source of truth
  useEffect(() => {
    const initAuth = async () => {
      // Check admin session — auth is via httpOnly cookie now;
      // we keep a non-sensitive `adminEmail` hint in localStorage to know whether
      // a session existed (the actual cookie is httpOnly so we can't read it).
      const adminEmailHint = localStorage.getItem('adminEmail');
      if (adminEmailHint) {
        setIsAdminAuthenticated(true);
      }
      
      // Always attempt cookie-based refresh first, before any routing
      try {
        const res = await fetch(`${API}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (res.ok) {
          const data = await res.json();
          if (data.success) {
            // Access token is now set as httpOnly cookie by the server
            setIsAuthenticated(true);
            console.log('[Auth] Session restored via refresh cookie');
          }
        } else {
          // Refresh failed - no valid session, clear stale local data
          console.log('[Auth] No valid session - refresh returned', res.status);
          localStorage.removeItem('user');
          localStorage.removeItem('userId');
          localStorage.removeItem('userEmail');
          localStorage.removeItem('userName');
        }
      } catch {
        // Network error - clear stale data
        console.log('[Auth] Refresh network error');
      }
      
      setIsLoading(false);
    };
    
    initAuth();
  }, []);

  const handleAdminLogin = () => {
    setIsAdminAuthenticated(true);
  };

  const handleAdminLogout = () => {
    setIsAdminAuthenticated(false);
  };

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <InstagramBrowserDetector>
        <div className="App">
          <InstallPWA />
          <InstallPrompt />
          <BrowserRouter>
            <AppRoutes
              isAuthenticated={isAuthenticated}
              isAdminAuthenticated={isAdminAuthenticated}
              isLoading={false}
              handleLogin={handleLogin}
              handleLogout={handleLogout}
              handleAdminLogin={handleAdminLogin}
              handleAdminLogout={handleAdminLogout}
            />
            {/* Bio Prompt Modal - shown once for users without bio */}
            {isAuthenticated && <BioPromptModal />}
          </BrowserRouter>
          <VersionChecker />
          <Toaster position="top-center" richColors />
        </div>
      </InstagramBrowserDetector>
    </ErrorBoundary>
  );
}

export default App;